import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthContext';
import { productionLogger } from '@/utils/production-logger';

interface ProductGridPreferences {
  viewSize: 'small' | 'large' | 'gallery';
  columnCount: 2 | 3 | 4;
}

const DEFAULT_PREFERENCES: ProductGridPreferences = {
  viewSize: 'large',
  columnCount: 4
};

const STORAGE_KEY = 'productGridPreferences';

export const useUserPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferencesState] = useState<ProductGridPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences from localStorage or database
  useEffect(() => {
    const loadPreferences = async () => {
      setIsLoading(true);
      
      if (user?.id) {
        // Logged in: load from database
        try {
          const { data, error } = await supabase
            .from('profiles')
            .select('preferences')
            .eq('id', user.id)
            .maybeSingle();

          if (error) throw error;

          const prefs = data?.preferences as any;
          if (prefs?.productGrid) {
            setPreferencesState(prefs.productGrid);
          } else {
            // No preferences in DB, check localStorage and migrate
            const localPrefs = localStorage.getItem(STORAGE_KEY);
            if (localPrefs) {
              const parsed = JSON.parse(localPrefs);
              setPreferencesState(parsed);
              // Migrate to database
              await saveToDatabase(parsed);
            } else {
              setPreferencesState(DEFAULT_PREFERENCES);
            }
          }
        } catch (error) {
          productionLogger.error('Error loading user preferences:', error);
          // Fallback to localStorage
          const localPrefs = localStorage.getItem(STORAGE_KEY);
          if (localPrefs) {
            setPreferencesState(JSON.parse(localPrefs));
          }
        }
      } else {
        // Not logged in: use localStorage
        const localPrefs = localStorage.getItem(STORAGE_KEY);
        if (localPrefs) {
          setPreferencesState(JSON.parse(localPrefs));
        } else {
          setPreferencesState(DEFAULT_PREFERENCES);
        }
      }
      
      setIsLoading(false);
    };

    loadPreferences();
  }, [user?.id]);

  // Save to database
  const saveToDatabase = async (prefs: ProductGridPreferences) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({ 
          id: user.id,
          preferences: { productGrid: prefs } as any,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'id'
        });

      if (error) throw error;
    } catch (error) {
      productionLogger.error('Error saving preferences to database:', error);
    }
  };

  // Update preferences
  const setPreferences = useCallback(async (newPrefs: Partial<ProductGridPreferences>) => {
    setPreferencesState(prev => {
      const updated = { ...prev, ...newPrefs };
      
      // Save to localStorage immediately
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      
      // Save to database if logged in
      if (user?.id) {
        saveToDatabase(updated);
      }
      
      return updated;
    });
  }, [user?.id]);

  const setViewSize = useCallback((viewSize: ProductGridPreferences['viewSize']) => {
    setPreferences({ viewSize });
  }, [setPreferences]);

  const setColumnCount = useCallback((columnCount: ProductGridPreferences['columnCount']) => {
    setPreferences({ columnCount });
  }, [setPreferences]);

  return {
    viewSize: preferences.viewSize,
    columnCount: preferences.columnCount,
    setViewSize,
    setColumnCount,
    isLoading
  };
};
